import { useState, useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { type Tag } from '@hungryroot/rest';
import { FOOD_PROFILE_NEVERS_DEEP_LINK } from '@hungryroot/routes';
import { MEAT_TAGS, SEAFOOD_TAGS, PLANTS_TAGS } from '../../utils/constants';
import { deepCloneObj } from '../../utils/utils';
import CollapsibleForm from '../CollapsibleForm.jsx';
import { type CustomerTag } from '../quiz/quiz';
import TagSelector from '../TagSelector.jsx';

interface Props {
  tags: Tag[];
  customerTags: CustomerTag[];
}

type TagGroupCategory = 'meatTags' | 'seafoodTags' | 'plantsTags';

// All ye who venture into ProteinPreferences, bless thy soul.
export default function ProteinPreferences({ tags, customerTags }: Props) {
  const meatTagsGroup = 'meatTags';
  const seafoodTagsGroup = 'seafoodTags';
  const plantsTagsGroup = 'plantsTags';

  const filterTagsByTypeInitialState = {
    [meatTagsGroup]: [],
    [seafoodTagsGroup]: [],
    [plantsTagsGroup]: [],
  };

  const [filteredTagsByType, setFilteredTagsByType] = useState(
    filterTagsByTypeInitialState
  );
  const [formVisible, setFormVisibility] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { hash } = useLocation();
  const isDeepLinked = hash.includes(FOOD_PROFILE_NEVERS_DEEP_LINK);

  // { [tagId]: preference }
  const [updatedPreferences, setUpdatedPreferences] = useState<
    Record<number, CustomerTag>
  >({});

  useLayoutEffect(() => {
    if (isDeepLinked) {
      setFormVisibility(true);
      sectionRef.current?.scrollIntoView({
        behavior: 'instant',
        block: 'start',
        inline: 'start',
      });
    }
  }, [isDeepLinked]);

  // Break apart the tags into groups for display in UI
  const tagsIdToGroup: Record<number, TagGroupCategory> = useMemo(() => {
    let tagToGroup: Record<number, TagGroupCategory> = {};
    MEAT_TAGS.forEach((tId) => (tagToGroup[tId] = meatTagsGroup));
    SEAFOOD_TAGS.forEach((tId) => (tagToGroup[tId] = seafoodTagsGroup));
    PLANTS_TAGS.forEach((tId) => (tagToGroup[tId] = plantsTagsGroup));
    return tagToGroup;
  }, []);

  // Divide the tags into groups for display in the correct section
  useEffect(() => {
    setFilteredTagsByType(
      tags.reduce((acc, t: { id: number }) => {
        const label = tagsIdToGroup[t.id];
        if (label) {
          acc[label].push(t);
        }
        return acc;
      }, deepCloneObj(filterTagsByTypeInitialState))
    );
  }, [tags, tagsIdToGroup]);

  const tagsSet = new Set(tags.map((t) => t.id));
  const initialCustomerTags = customerTags.filter((tag) => tagsSet.has(tag.id));

  const formatTagsText = useMemo(() => {
    // Combines original values but overwrites values with updated preferences
    const combinedCustomerTags = [
      ...initialCustomerTags,
      ...Object.values(updatedPreferences),
    ].reduce(
      (acc, tag) => {
        acc[tag.id] = tag;
        return acc;
      },
      {} as Record<number, CustomerTag>
    );

    if (Object.keys(combinedCustomerTags).length) {
      // Show So-So and Absolutely tags
      const desiredTagNames = Object.values(combinedCustomerTags)
        .filter(({ preference }) => preference >= 0)
        .map((tag) => tag.name);

      if (desiredTagNames.length) {
        // Alphabetize the tags and join them with a comma
        return desiredTagNames.sort().join(', ');
      }

      const hasNeverTags =
        Object.values(combinedCustomerTags).filter(
          ({ preference }) => preference === -1
        ).length > 0;

      if (hasNeverTags) {
        return 'No thanks';
      }
    }

    return 'Set your preferences.';
  }, [initialCustomerTags, updatedPreferences]);

  const onTagSelect = (tagOption: string) => {
    const splitTagOption = tagOption.split(' ');

    // Find matching tag or bail early
    const tag = tags.find(({ id }) => id === Number(splitTagOption[0]));
    if (!tag) return;

    setUpdatedPreferences({
      ...updatedPreferences,
      [tag.id]: {
        ...tag,
        is_explicit: true,
        preference: Number(splitTagOption[1]) as 0 | 1 | -1,
      },
    });
  };

  return (
    <div ref={sectionRef}>
      <CollapsibleForm
        title="Protein Preferences"
        text={formatTagsText}
        iconDesktop="/public/img/preferences/protein_icon.svg"
        iconMobile={null}
        contentVisible={formVisible}
        toggleForm={() => setFormVisibility(!formVisible)}
        dataCy="protein-preferences"
      >
        <div className="protein-preferences" data-testid="protein-preferences">
          <div className="hr-mb-32_fixed hr-center-txt">
            <div className="hr-paragraph hr-mb-8">
              Do you like these kinds of proteins?
            </div>
            <div className="hr-subtitle-md">
              We'll use your preferences to suggest more items that suit your
              tastes.
            </div>
          </div>
          <TagSelector
            tags={filteredTagsByType[meatTagsGroup]}
            customerTags={customerTags}
            onSelect={() => {}} // unused
            onTagSelect={onTagSelect}
          />
          {filteredTagsByType[meatTagsGroup].length ? (
            <div className="hr-line-separator hr-mb-16_fixed"></div>
          ) : null}
          <TagSelector
            tags={filteredTagsByType[seafoodTagsGroup]}
            customerTags={customerTags}
            onSelect={() => {}} //  unused
            onTagSelect={onTagSelect}
          />
          {filteredTagsByType[seafoodTagsGroup].length ? (
            <div className="hr-line-separator hr-mb-16_fixed"></div>
          ) : null}
          <TagSelector
            tags={filteredTagsByType[plantsTagsGroup]}
            customerTags={customerTags}
            onSelect={() => {}} // unused
            onTagSelect={onTagSelect}
          />
          <button
            data-testid="protein-preferences-update-button"
            className="hr-btn-blue hr-max"
            disabled={Object.keys(updatedPreferences).length === 0}
            onClick={() => {
              setFormVisibility(false);
            }}
          >
            Update
          </button>
        </div>
      </CollapsibleForm>
    </div>
  );
}
